import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IListFilter } from '@app/api/interfaces/common/list-filter';
import { IPaginationMeta, IPaginationRequest } from '@app/api/interfaces/common/pagination-meta.interface';
import { prepareParamsForRequest } from '@app/core/methods/http-params';
import { SplitRequestService } from '@app/core/services/split-request-by-limit/split-request.service';
import {
  ILoggerDeploymentData,
  ILoggerDeploymentDataDetailed,
} from '@app/api/interfaces/logger-deployment-data.interface';
import { IComment, IUpdateCommentRequest } from '@app/api/interfaces/comment.interface';
import { LoggerDeploymentsDataMockService } from '@mock/data-services/logger-deployments-data-mock.service';

@Injectable({
  providedIn: 'root',
})
export class LoggerDeploymentsDataApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly splitRequestService: SplitRequestService,
    private readonly MOCK: LoggerDeploymentsDataMockService
  ) {}

  public getByProjectId(
    projectId: string,
    filter: IListFilter = {},
    pagination: Partial<IPaginationRequest> = {
      limit: 1000,
    },
    all = true
  ): Observable<IApiResponse<(ILoggerDeploymentData | ILoggerDeploymentDataDetailed)[], IPaginationMeta>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.getAll(projectId, filter);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data`;

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
        ...(pagination || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    if (all) {
      return this.splitRequestService.get<ILoggerDeploymentData | ILoggerDeploymentDataDetailed, IPaginationMeta>(
        url,
        options
      );
    } else {
      return this.httpClient.get<
        IApiResponse<(ILoggerDeploymentData | ILoggerDeploymentDataDetailed)[], IPaginationMeta>
      >(url, options);
    }
  }

  public getOne(
    projectId: string,
    deploymentId: string
  ): Observable<IApiResponse<ILoggerDeploymentDataDetailed, object>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.getOne(deploymentId);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}`;

    return this.httpClient.get<IApiResponse<ILoggerDeploymentDataDetailed, object>>(url, {
      withCredentials: true,
    });
  }

  public updateComment(
    projectId: string,
    deploymentId: string,
    body: IUpdateCommentRequest
  ): Observable<IApiResponse<IComment, object>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.updateComment(deploymentId, body);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/comment`;

    return this.httpClient.patch<IApiResponse<IComment, object>>(url, body, {
      withCredentials: true,
    });
  }
}
